import {
  emailValidation as _emailValidation,
  ErrorPresentation,
  notEmptyArrayValidation,
  requiredValidation,
  stringMaxValidation,
  stringMinMaxValidation,
} from 'helpers/validators';
import {
  UserCreateOnView,
  UserEditOnView,
} from 'shared/domain/user/types/view';
import { FieldSize } from 'shared/types/fieldSize';
import { UserRole } from 'shared/types/userRole';
import { ValidationSchema } from '../types';
import { noValidation, validate } from '../validation';
import { ValidationTranslationKeys } from 'intl/keys';

export const editUserValidationSchema: ValidationSchema<UserEditOnView> = {
  _id: noValidation,
  label: labelValidation,
  phone: phoneValidation,
  email: emailValidation,
  role: roleValidation,
  sites: sitesValidation,
  processes: processesValidation,
  personalData: noValidation,
  canBePersonalDataModified: noValidation,
};

export function validateEditUser(
  key: keyof UserEditOnView,
  value: UserEditOnView[keyof UserEditOnView]
): ErrorPresentation {
  return validate(key, value, editUserValidationSchema);
}

export function getCreateUserValidationSchema(
  emails: string[]
): ValidationSchema<UserCreateOnView> {
  return {
    email: makeUniqueEmailValidation(emails),
    role: roleValidation,
    sites: sitesValidation,
    processes: processesValidation,
  };
}

function labelValidation(value: unknown): ErrorPresentation {
  return (
    requiredValidation(value) || stringMinMaxValidation(FieldSize.M, value)
  );
}

function phoneValidation(value: unknown): ErrorPresentation {
  return stringMaxValidation(FieldSize.S, value);
}

function emailValidation(value: unknown): ErrorPresentation {
  return requiredValidation(value) || _emailValidation(value);
}

function roleValidation(value: unknown): ErrorPresentation {
  return requiredValidation(value);
}

function sitesValidation(
  value: unknown,
  allValues: UserCreateOnView
): ErrorPresentation {
  if (allValues?.role._id === UserRole.visible_no_access) {
    return;
  }
  return requiredValidation(value) || notEmptyArrayValidation(value);
}

function processesValidation(
  value: unknown,
  allValues: UserCreateOnView
): ErrorPresentation {
  if (allValues?.role._id === UserRole.visible_no_access) {
    return;
  }
  return requiredValidation(value) || notEmptyArrayValidation(value);
}

function makeUniqueEmailValidation(
  emails: string[]
): (value: unknown) => ErrorPresentation {
  const uniqueValidation = (value: string): ErrorPresentation => {
    if (emails.includes(value.toLocaleLowerCase())) {
      return {
        helperText: { id: ValidationTranslationKeys.userAlreadyExists },
      };
    }
    return undefined;
  };

  return function uniqueEmailValidation(
    value: unknown
  ): ErrorPresentation {
    return emailValidation(value) || uniqueValidation(value as string);
  };
}
